import { transient } from 'aurelia-framework';
import type { DynTable } from './dyn-table';

/**
 * Column resize for `resizable` headers (port of the aurelia-headless ui-table-column
 * resize). Injects a handle span into each resizable header; dragging it feeds
 * DynTable.setColumnWidth, which renders widths as scoped nth-child CSS. The handle's own
 * pointerdown listener runs before the column drag's container listener, so its
 * stopPropagation keeps a resize from arming a column drag.
 */
@transient()
export class DynTableColumnResize implements EventListenerObject {
  private table: DynTable;
  private handles: HTMLElement[] = [];
  private th?: HTMLElement;
  private pointerId?: number;
  private startX = 0;
  private startWidth = 0;
  /** The width set by the latest move; persisted as-is on release. Re-measuring the
   *  header at pointerup reads back table-layout quirks (collapsed borders, rounding)
   *  and makes the size jump. */
  private lastWidth?: number;

  attach(table: DynTable): void {
    this.table = table;
    this.injectHandles();
  }

  /** (Re)inject handles - called again after the consumer re-renders the table. */
  injectHandles(): void {
    if (!this.table) {
      return;
    }
    this.handles = this.handles.filter(handle => handle.isConnected);
    for (const th of this.table.headerCells()) {
      if (!this.table.columnFlag(th, 'resizable') || th.querySelector(':scope > .dyn-table__resize-handle')) {
        continue;
      }
      const handle = document.createElement('span');
      handle.classList.add('dyn-table__resize-handle');
      handle.setAttribute('aria-hidden', 'true');
      handle.addEventListener('pointerdown', this);
      th.appendChild(handle);
      this.handles.push(handle);
    }
  }

  detach(): void {
    this.endResize();
    for (const handle of this.handles) {
      handle.removeEventListener('pointerdown', this);
      handle.remove();
    }
    this.handles = [];
  }

  handleEvent(event: Event): void {
    switch (event.type) {
      case 'pointerdown':
        this.onPointerDown(event as PointerEvent);
        break;
      case 'pointermove':
        this.onPointerMove(event as PointerEvent);
        break;
      case 'pointerup':
      case 'pointercancel':
        this.onPointerUp(event as PointerEvent);
        break;
    }
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.pointerId !== undefined || event.button !== 0) {
      return;
    }
    const th = event.target instanceof Element ? event.target.closest('th') : null;
    if (!th || !this.table.headerCells().includes(th)) {
      return;
    }
    // The column drag listens on the container; a resize must never arm it.
    event.stopPropagation();
    event.preventDefault();
    this.pointerId = event.pointerId;
    this.th = th;
    this.startX = event.clientX;
    this.startWidth = th.getBoundingClientRect().width;
    this.lastWidth = undefined;
    window.addEventListener('pointermove', this);
    window.addEventListener('pointerup', this);
    window.addEventListener('pointercancel', this);
  }

  private onPointerMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId || !this.th) {
      return;
    }
    this.lastWidth = Math.max(0, this.startWidth + event.clientX - this.startX);
    this.table.setColumnWidth(this.table.columnKey(this.th), this.lastWidth);
  }

  private onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId || !this.th) {
      return;
    }
    if (this.lastWidth !== undefined) {
      this.table.setColumnWidth(this.table.columnKey(this.th), this.lastWidth, true);
    }
    this.endResize();
  }

  private endResize(): void {
    this.th = undefined;
    this.pointerId = undefined;
    window.removeEventListener('pointermove', this);
    window.removeEventListener('pointerup', this);
    window.removeEventListener('pointercancel', this);
  }
}
